import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
// 3.27 (U37): labels sit ABOVE the field (style B) — Material floats them always.
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './config/msal.config';
import { authInterceptor, authTokenInterceptor, loadingInterceptor } from './core/interceptors';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { assertParentPortalSsoConfig } from '../environments/environment.shared';

// Fail LOUDLY at startup when the Parent Portal's SSO client IDs are still
// placeholders (task 4.2) — a visibly broken startup beats silently dead
// login buttons. Google's empty client ID only warns (button is hidden).
assertParentPortalSsoConfig();

// MSAL instance factory
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    // add-parent-portal-pwa 1.1: app files only (ngsw-config.json has no dataGroups:
    // no API response is ever cached on the phone).
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimations(),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    provideHttpClient(
      withInterceptors([loadingInterceptor, authInterceptor, authTokenInterceptor]),
    ),

    // MSAL Configuration using centralized config
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // Global error handler
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
};
